import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { WheelSection } from '../types/goals';
import * as db from '../services/dbService';
import { DEFAULT_WHEEL_SECTIONS } from '../data/defaultWheelSections';
import { useAuth } from '../context/AuthContext';
import { queryKeys } from '../lib/queryKeys';
import { prefixedId } from '../utils/idUtils';
import { normalizeWheelSectionsForPersistence } from '../utils/wheelSectionUtils';

export function useWheelLife(options?: { enabled?: boolean }) {
  const { userId } = useAuth();
  const qc = useQueryClient();
  const key = queryKeys.wheelSections(userId);

  const { data: sections = DEFAULT_WHEEL_SECTIONS, isLoading: loading } = useQuery({
    queryKey: key,
    queryFn: async () => {
      const data = await db.fetchWheelSections();
      return data.length > 0 ? data : DEFAULT_WHEEL_SECTIONS.map(section => ({ ...section }));
    },
    enabled: (options?.enabled ?? true) && !!userId,
    staleTime: 5 * 60 * 1000,
  });

  const saveMutation = useMutation({
    mutationFn: (updated: WheelSection[]) => db.saveWheelSections(updated),
    onMutate: async (updated) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<WheelSection[]>(key);
      qc.setQueryData(key, updated);
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
    },
  });

  const persist = (updated: WheelSection[]) => {
    const normalized = normalizeWheelSectionsForPersistence(updated);
    return saveMutation.mutateAsync(normalized);
  };

  const updateRating = (id: string, rating: number) =>
    persist(sections.map(s => (s.id === id ? { ...s, rating } : s)));

  const updateSection = (id: string, changes: Partial<Omit<WheelSection, 'id'>>) =>
    persist(sections.map(s => (s.id === id ? { ...s, ...changes } : s)));

  const addSection = async (name: string, color: string) => {
    const newSection = { id: prefixedId('sec'), name, color, rating: 0 };
    await persist([...sections, newSection]);
    return newSection;
  };

  const removeSection = (id: string) =>
    persist(sections.filter(s => s.id !== id));

  return { sections, loading, updateRating, updateSection, addSection, removeSection };
}
